import { memberProfilePhotoFilename } from '../components/MemberProfileAvatar';

/** Same basename rules as server `GET /api/uploads/:filename`. */
function normalizeUploadFilename(profilePicture) {
  const raw = memberProfilePhotoFilename(profilePicture);
  if (!raw) return null;
  const base = raw.split('/').pop().split('?')[0].trim();
  if (!base || /[^a-zA-Z0-9._-]/.test(base)) return null;
  return base;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/** filename -> string | null (cached hits and known 404s) */
const resolvedCache = new Map();
/** filename -> Promise — dedupe concurrent fetches */
const inflight = new Map();

/**
 * Fetches a member profile image as a data URL for ID card export (authenticated api).
 * Uses validateStatus so 404 does not throw; caches success and “missing” to avoid
 * hammering the API when exporting many cards or clicking download twice.
 *
 * 404 means the file is not on the server (e.g. Render ephemeral disk cleared, or DB
 * references files never migrated). Cards still render with initials when this returns null.
 *
 * @param {import('axios').AxiosInstance} api
 * @param {string | null | undefined} profilePicture
 * @returns {Promise<string | null>}
 */
/**
 * For `/members/cards/data` with `include_profile_blobs`, the server already resolves each
 * photo from blob store or disk. When `profile_photo_data_url` is present on the member
 * object (including `null` for missing files), use it and do not call GET /uploads again —
 * that avoids 404 noise and duplicate work; cards use initials when this is null.
 *
 * If the field is absent (legacy responses), falls back to {@link fetchMemberUploadPhotoDataUrl}.
 *
 * @param {import('axios').AxiosInstance} api
 * @param {Record<string, unknown>} member
 * @returns {Promise<string | null>}
 */
export async function resolveMemberCardPhotoDataUrl(api, member) {
  if (Object.prototype.hasOwnProperty.call(member, 'profile_photo_data_url')) {
    const p = member.profile_photo_data_url;
    if (typeof p === 'string' && p.startsWith('data:')) return p;
    return null;
  }
  return fetchMemberUploadPhotoDataUrl(api, member.profile_picture);
}

export async function fetchMemberUploadPhotoDataUrl(api, profilePicture) {
  const filename = normalizeUploadFilename(profilePicture);
  if (!filename) return null;

  if (resolvedCache.has(filename)) {
    return resolvedCache.get(filename);
  }
  if (inflight.has(filename)) {
    return inflight.get(filename);
  }

  const promise = (async () => {
    try {
      const response = await api.get(`/uploads/${filename}`, {
        responseType: 'blob',
        validateStatus: (status) => status === 200 || status === 404,
      });
      if (response.status === 404 || !response.data?.size) {
        resolvedCache.set(filename, null);
        return null;
      }
      const dataUrl = await blobToDataUrl(response.data);
      resolvedCache.set(filename, dataUrl);
      return dataUrl;
    } catch {
      // Network / 5xx — do not cache so a retry can succeed
      return null;
    } finally {
      inflight.delete(filename);
    }
  })();

  inflight.set(filename, promise);
  return promise;
}

export function clearMemberUploadPhotoCache() {
  resolvedCache.clear();
  inflight.clear();
}
